// TEMPORARY (NVCMP): product info comparison test page — remove when COBOL product info is retired.
import React, { useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  compareProductInfo,
  mergeCompareResults,
  CompareDifference,
  CompareInvalidProduct,
  CompareProduct,
  ProductInfoCompareResult,
} from '../../../felles/APICalls_NVCMP';
import CSharpUtils from '../../../felles/CSharpUtils';

/**
 * Sammenligner gammel vareinfo (COBOL) med ny (/newinfo) for samme kunde og varer.
 * Avvikene (rød) kommer fra sammenligningen i backend, med de samme toleransene og kjente
 * avvikene som sammenligningsloggen bruker — verdiene vises slik de kom fra hver side.
 */

/** warn = lagerfelt: ulikhet er en advarsel (gul), ikke et avvik — se warehouseRows. */
type FieldDef = {
  key: string;
  label: string;
  value: (p: CompareProduct) => unknown;
  warn?: boolean;
  /** JSON-navnet i gammel (/infos) og ny (/newinfo) når det ikke er key i camelCase. */
  oldName?: string;
  newName?: string;
};

/** key = feltnavnet backend bruker i avvikene. */
const FIELDS: FieldDef[] = [
  { key: 'Alpha', label: 'Alfa', value: p => p.alpha },
  { key: 'ProductNumber', label: 'Artikkelnummer', value: p => p.productNumber },
  { key: 'AltAlpha', label: 'Alt. alfa', value: p => p.altAlpha },
  { key: 'AltProductNumber', label: 'Alt. artikkelnummer', value: p => p.altProductNumber },
  { key: 'EdbNumber', label: 'EDB-nummer', value: p => p.edbNumber },
  { key: 'ProductDescription', label: 'Varebetegnelse', value: p => p.productDescription },
  { key: 'Price', label: 'Nettopris', value: p => p.price },
  { key: 'OrderPrice', label: 'Ordrepris', value: p => p.orderPrice },
  { key: 'SellingPrice', label: 'Utsalgspris', value: p => p.sellingPrice },
  { key: 'RecommendedPrice', label: 'Veiledende pris', value: p => p.recommendedPrice },
  { key: 'PriceADKasse', label: 'Pris AD-kasse', value: p => p.priceADKasse },
  { key: 'OrderFullCost', label: 'Kostpris', value: p => p.orderFullCost },
  { key: 'Discount1', label: 'Rabatt 1', value: p => p.discount1 },
  { key: 'Discount2', label: 'Rabatt 2', value: p => p.discount2 },
  { key: 'Discount3', label: 'Rabatt 3', value: p => p.discount3 },
  { key: 'Campaign1.Campaign', label: 'Kampanje 1 – antall', value: p => p.campaign1?.campaign },
  { key: 'Campaign1.Price', label: 'Kampanje 1 – pris', value: p => p.campaign1?.price },
  { key: 'Campaign2.Campaign', label: 'Kampanje 2 – antall', value: p => p.campaign2?.campaign },
  { key: 'Campaign2.Price', label: 'Kampanje 2 – pris', value: p => p.campaign2?.price },
  { key: 'Surcharge', label: 'Pristillegg', value: p => p.surcharge },
  { key: 'SurchargeCode', label: 'Pristillegg kode', value: p => p.surchargeCode },
  { key: 'SurchargeType', label: 'Pristillegg type', value: p => p.surchargeType },
  { key: 'DiscountInfo', label: 'Brekkasje %', value: p => p.discountInfo },
  { key: 'CartInholdPerPackage', label: 'Kartonginnhold', value: p => p.cartInholdPerPackage },
  { key: 'BestInfo', label: 'Antall i bestilling', value: p => p.bestInfo },
  { key: 'OrderDeliveryTime', label: 'Forventet levering (år/uke)', value: p => p.orderDeliveryTime },
  { key: 'Note', label: 'Merknad', value: p => p.note },
  { key: 'Location', label: 'Lokasjon', value: p => p.location },
  { key: 'Text', label: 'Tekst', value: p => p.text },
  { key: 'TireMarkingInfo', label: 'Dekkmerking', value: p => p.tireMarkingInfo },
  { key: 'Customs', label: 'Toll (opprinnelse:tolltariff)', value: p => p.customs },
  { key: 'Ean', label: 'EAN', value: p => p.ean },
  { key: 'Stock', label: 'Lager (10)', value: p => p.stock, warn: true },
  { key: 'TotalStock', label: 'Totalt lager', value: p => p.totalStock, warn: true },
];

const show = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return v.toLocaleString('nb-NO', { maximumFractionDigits: 4 });
  return String(v).trim();
};

/** Én vare per linje: alfa+artikkelnummer, eventuelt etterfulgt av antall. */
const parseProducts = (input: string): { productId: string; quantity: number }[] =>
  input
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const [productId, qty] = line.split(/[\s;\t]+/);
      const quantity = qty ? Number(qty.replace(',', '.')) : 1;
      return { productId, quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1 };
    });

type Product = ReturnType<typeof parseProducts>[number];

const formatProducts = (products: Product[]): string => products.map(p => `${p.productId} ${p.quantity}`).join('\n');

/** En forespørsel til /infos slik den logges, f.eks. {"companyId":"618","customerNumber":"147268","adk":false,"products":[...]}. */
type PastedRequest = { companyId: string; customerNumber: string; adk: boolean; products: Product[] };

/** Tolker innlimt tekst som en JSON-forespørsel; null når det ikke er en (da limes teksten inn som vanlig). */
const parsePastedRequest = (text: string): PastedRequest | null => {
  if (!text.trim().startsWith('{')) return null;
  let json: any;
  try {
    json = JSON.parse(text);
  } catch {
    return null;
  }
  if (!json || !Array.isArray(json.products)) return null;
  const products = json.products
    .map((p: any): Product => {
      // productId er alfa+artikkelnummer med skilletegn; alphaArt uten. alphaArt brukes hvis productId har mellomrom,
      // siden varelisten skiller vare og antall med mellomrom.
      const id = String(p?.productId ?? '').trim();
      const productId = id && !/\s/.test(id) ? id : String(p?.alphaArt ?? '').replace(/\s/g, '');
      const quantity = Number(p?.quantity);
      return { productId, quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1 };
    })
    .filter((p: Product) => p.productId);
  return {
    companyId: String(json.companyId ?? '').replace(/\D/g, '').slice(0, 3),
    customerNumber: String(json.customerNumber ?? '').replace(/\D/g, ''),
    adk: json.adk === true,
    products,
  };
};

/** Legger varene til listen; finnes varen fra før (samme alfa+artikkelnummer uten skilletegn), summeres antallet. */
const addProducts = (existing: Product[], added: Product[]): Product[] => {
  const key = (p: Product) => unformat(p.productId);
  const result = existing.map(p => ({ ...p }));
  for (const p of added) {
    const match = result.find(r => key(r) === key(p));
    if (match) match.quantity += p.quantity;
    else result.push({ ...p });
  }
  return result;
};

/**
 * Lagerbeholdning per lager fra begge sider. Alle lager som finnes på én av sidene vises, i rekkefølgen de kom; tomt
 * der varen mangler på den siden (ingen verdier). Lagerkoden kommer bare fra ny (gammel returnerer den ikke) og vises foran navnet,
 * f.eks. "10-Showroom".
 * Ulikheter er advarsler, ikke avvik: beholdningen kan endre seg mellom de to kallene, og gammel og ny tar med og
 * utelater ulike lager (ny viser bevisst bare de interne lagrene som har navn, ingen leverandørlager). Backend gir de
 * samme advarslene (differences med isWarning). Advarsel når lageret bare finnes på én
 * side (også med beholdning 0 — et manglende lager er ikke det samme som 0), eller når beholdningen er ulik.
 * Linjene pares én og én som i backend (ProductInfoComparison.CompareWarehouses): samme navn og beholdning først, så samme
 * navn. Navnet alene er ikke unikt: et eget lager og en leverandør kan hete det samme (92 SKIEN og 963 SKIEN), så hver
 * linje i ny er sin egen rad.
 */
const warehouseRows = (cobol?: CompareProduct, handler?: CompareProduct) => {
  type Line = NonNullable<CompareProduct['warehouses']>[number];
  const name = (w: Line) => (w.warehouse ?? '').trim();
  const sameStock = (a: Line, b: Line) => (a.stock ?? 0) === (b.stock ?? 0);
  const cobolLines = (cobol?.warehouses ?? []).filter(w => name(w));
  const handlerLines = (handler?.warehouses ?? []).filter(w => name(w));

  const pairs = new Map<Line, Line>();
  const unpaired = new Set(handlerLines);
  const pairUp = (matches: (c: Line, h: Line) => boolean) => {
    for (const c of cobolLines) {
      if (pairs.has(c)) continue;
      const h = handlerLines.find(x => unpaired.has(x) && name(x) === name(c) && matches(c, x));
      if (!h) continue;
      pairs.set(c, h);
      unpaired.delete(h);
    }
  };
  pairUp(sameStock);
  pairUp(() => true);

  // Eksternt lager (leverandør): firmaet foran, og lagerkoden bare når linjen er ett lager (ikke total), f.eks. "722/10-R.MOLDE"
  const label = (c?: Line, h?: Line) => {
    const line = (h ?? c)!;
    const code = line.warehouseType === 'External'
      ? [line.companyId, line.warehouseCode].filter(Boolean).join('/')
      : (line.warehouseCode ?? '').trim();
    return code ? `${code}-${name(line)}` : name(line);
  };
  const row = (c?: Line, h?: Line) => ({
    label: label(c, h),
    cobol: !cobol ? '' : show(c?.stock),
    handler: !handler ? '' : show(h?.stock),
    isWarning: !!cobol && !!handler && (!c || !h || !sameStock(c, h)),
  });
  return [
    ...cobolLines.map(c => row(c, pairs.get(c))),
    ...handlerLines.filter(h => unpaired.has(h)).map(h => row(undefined, h)),
  ];
};

/** Lagerfeltene (Lager, Totalt lager) som er ulike — advarsler på samme måte som lagerlinjene. */
const stockFieldWarnings = (cobol?: CompareProduct, handler?: CompareProduct) =>
  new Set(
    !cobol || !handler
      ? []
      : FIELDS.filter(f => f.warn && show(f.value(cobol) ?? 0) !== show(f.value(handler) ?? 0)).map(f => f.key),
  );

const warningCountFor = (cobol?: CompareProduct, handler?: CompareProduct) =>
  warehouseRows(cobol, handler).filter(w => w.isWarning).length + stockFieldWarnings(cobol, handler).size;

/** Lys rød bakgrunn for avvik: tittellinjen på varen og feltene som er ulike. */
const DIFF_BACKGROUND = '#ffebee';
/** Lys gul bakgrunn for advarsler (lager): tittellinjen på varer uten avvik, og lagerlinjene som er ulike. */
const WARN_BACKGROUND = '#fff8e1';
/** Lys grå bakgrunn ved mismatch for linjene som ikke sammenlignes (felt utenfor MISMATCH_FIELDS og lagerlinjene). */
const UNRELATED_BACKGROUND = '#f5f5f5';

/**
 * Egenskapen slik den heter i JSON-svaret (camelCase per ledd), f.eks. "Campaign1.Price" → "campaign1.price".
 * Gammel (/infos, Product) og ny (/newinfo, GetProductInfoItem) bruker i dag de samme navnene.
 */
const jsonName = (path: string) => path.split('.').map(s => s.charAt(0).toLowerCase() + s.slice(1)).join('.');
/** Ett navn når gammel og ny heter det samme, ellers "gammel / ny". */
const propertyNames = (oldPath: string, newPath: string) =>
  jsonName(oldPath) === jsonName(newPath) ? jsonName(oldPath) : `${jsonName(oldPath)} / ${jsonName(newPath)}`;
const PROPERTY_CELL = { color: 'text.secondary', fontFamily: 'monospace', whiteSpace: 'nowrap' } as const;
/** Kolonneoverskriften nevner gammel / ny bare når minst ett felt heter forskjellig. */
const NAMES_DIFFER = FIELDS.some(f => jsonName(f.oldName ?? f.key) !== jsonName(f.newName ?? f.key));

/** Tid i en lesbar enhet: "320 ms", "12,3 s", "8 min 15 s". */
const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toLocaleString('nb-NO', { maximumFractionDigits: 1 })} s`;
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} s`;
};

/** "8 kall, snitt 28,4 s, lengste 31,2 s" — hvert COBOL-kall er én del av varene. */
const callSummary = (callsMs: number[]): string =>
  `${callsMs.length} kall, snitt ${formatDuration(callsMs.reduce((n, ms) => n + ms, 0) / callsMs.length)}, ` +
  `lengste ${formatDuration(Math.max(...callsMs))}`;

/** Varer per kall til backend (og dermed per COBOL-kall), så hvert kall holder seg godt under tidsgrensene. */
const BATCH_SIZE = 25;

const productKey = (p?: CompareProduct | null) => p?.edbNumber ?? `${p?.alpha ?? ''}${p?.productNumber ?? ''}`;

/** Artikkelnummeret uten skilletegn, som backend (AlphaNumberType.Parse) sender det til begge sider. */
const unformat = (s?: string | null) => (s ?? '').replace(/[^a-zA-Z0-9æøåÆØÅ]/g, '').toUpperCase();

/** varekey_file har bare de første 14 tegnene av artikkelnummeret uten skilletegn. */
const LOOKUP_LENGTH = 14;

/** Ved mismatch er det to ulike varer: bare feltene som viser hvilken vare hver side fant, sammenlignes. */
const MISMATCH_FIELDS = new Set(['Alpha', 'ProductNumber', 'EdbNumber', 'AltAlpha', 'AltProductNumber', 'ProductDescription']);

/**
 * En rad: samme vare (EDB) fra begge sider, eller — mismatch — gammel og ny svarte med hver sin vare for samme
 * etterspurte vare (requested = alfa+artikkelnummer slik det ble sendt).
 */
type Pair = { cobol?: CompareProduct; handler?: CompareProduct; requested?: string; mismatch?: boolean };

/** COBOL-programmet ProductInfoService.FindProductInfo velger (firma = supplierNumber på testsiden). */
const cobolProgram = (companyId: string, adk: boolean) =>
  adk ? 'WWWVADKS' : ({ '956': 'WWWV956', '912': 'wwwkrom', '913': 'WWWV913' } as Record<string, string>)[companyId] ?? 'WWWVINFO';

/** Rå verdi til prompten (uten norsk tallformat), "(empty)" når den mangler. */
const raw = (v: unknown): string => (v === null || v === undefined || String(v).trim() === '' ? '(empty)' : String(v).trim());

/** Det som trengs fra siden for å lage en AI-prompt for én rad (vare). */
type PromptContext = {
  pair: Pair;
  key: string;
  companyId: string;
  customerNumber: number;
  adk: boolean;
  /** Varene som ble sendt i sammenligningen (med antall). */
  compared: Product[];
  /** Avvikene fra backend som gjelder denne varen (feltavvik og generelle). */
  differences: CompareDifference[];
  invalid: { cobol: CompareInvalidProduct[]; handler: CompareInvalidProduct[] };
};

/**
 * En prompt (engelsk) som lar en AI-assistent med tilgang til repoet undersøke én rad: forespørselen (også som JSON
 * som kan limes inn her igjen), hva som er ulikt, begge sidenes svar og hvor i koden hver side kommer fra.
 */
const buildInvestigationPrompt = ({ pair, key, companyId, customerNumber, adk, compared, differences, invalid }: PromptContext): string => {
  const { cobol, handler } = pair;
  const isMismatch = !!pair.mismatch;
  const program = cobolProgram(companyId, adk);

  // Etterspurte varer for raden: gammel gjentar etterspurt alfa+artikkelnummer (varens eget står i Alt*-feltene).
  const idOf = (alpha?: string | null, number?: string | null) => `${(alpha ?? '').trim().toUpperCase()}${unformat(number)}`;
  const candidates = new Set(
    pair.requested
      ? [idOf(pair.requested.slice(0, 3), pair.requested.slice(3))]
      : [idOf(cobol?.alpha, cobol?.productNumber), idOf(cobol?.altAlpha, cobol?.altProductNumber), idOf(handler?.alpha, handler?.productNumber)],
  );
  const requested = compared.filter(p => candidates.has(idOf(p.productId.slice(0, 3), p.productId.slice(3))));
  const requestedText = requested.length
    ? requested.map(p => `${p.productId} (quantity ${p.quantity})`).join(', ')
    : `not identified among the requested products (EDB ${key})`;
  const reproduce = JSON.stringify({
    companyId,
    customerNumber: String(customerNumber),
    adk,
    products: (requested.length ? requested : [{ productId: `${cobol?.alpha ?? handler?.alpha ?? ''}${cobol?.productNumber ?? handler?.productNumber ?? ''}`, quantity: 1 }])
      .map(p => ({ productId: p.productId, alphaArt: unformat(p.productId), quantity: p.quantity })),
  });

  const fieldDiffs = isMismatch
    ? FIELDS.filter(f => MISMATCH_FIELDS.has(f.key) && show(f.value(cobol!)) !== show(f.value(handler!)))
    : FIELDS.filter(f => differences.some(d => !d.isWarning && d.field === f.key));
  const stockWarnings = isMismatch ? new Set<string>() : stockFieldWarnings(cobol, handler);
  const warehouseWarnings = isMismatch ? [] : warehouseRows(cobol, handler).filter(w => w.isWarning);
  const unknownDiffs = differences.filter(d => !d.isWarning && d.field && !FIELDS.some(f => f.key === d.field));

  const outcome = isMismatch ? 'MISMATCH: old and new answered with different products for the same requested product'
    : !cobol ? 'MISSING IN OLD: only the new handler returned this product'
    : !handler ? 'MISSING IN NEW: only COBOL returned this product'
    : fieldDiffs.length || unknownDiffs.length ? `DIFFERENCES in ${fieldDiffs.length + unknownDiffs.length} field(s)`
    : stockWarnings.size || warehouseWarnings.length ? 'STOCK WARNINGS only (no differences)'
    : 'EQUAL (no differences)';

  const rows = [
    ...fieldDiffs.map(f => `| ${f.label} | ${jsonName(f.key)} | ${raw(cobol ? f.value(cobol) : undefined)} | ${raw(handler ? f.value(handler) : undefined)} | difference |`),
    ...unknownDiffs.map(d => `| ${d.field} | ${jsonName(d.field ?? '')} | ${raw(d.cobol)} | ${raw(d.handler)} | difference |`),
    ...FIELDS.filter(f => stockWarnings.has(f.key))
      .map(f => `| ${f.label} | ${jsonName(f.key)} | ${raw(cobol ? f.value(cobol) : undefined)} | ${raw(handler ? f.value(handler) : undefined)} | stock warning |`),
    ...warehouseWarnings.map(w => `| Warehouse ${w.label} | warehouses[].stock | ${raw(w.cobol)} | ${raw(w.handler)} | stock warning |`),
  ];
  const invalidText = [
    ...invalid.cobol.map(p => `- Old reported invalid: ${p.alfa ?? ''}${p.artikkelnummer ?? ''} ${p.feilmelding ?? ''}`.trimEnd()),
    ...invalid.handler.map(p => `- New reported invalid: ${p.productId ?? ''} (${p.feilmelding ?? ''})`),
  ];

  return [
    'Investigate a product info comparison result from the NVCMP test page in Autodata-adApi: the old product info',
    '(COBOL) versus the new GetProductInfosHandler (/newinfo), called with the same request.',
    '',
    '## Request',
    `- Environment: ${window.location.host}`,
    `- Company ${companyId}, customer ${customerNumber}, ADK ${adk ? 'yes' : 'no'}`,
    `- COBOL program: ${program}`,
    `- Requested: ${requestedText}. Both sides get the id without separators (AlphaNumberType.Parse).`,
    '- Reproduce on the NVCMP page by pasting this request:',
    '```json',
    reproduce,
    '```',
    '',
    '## Result',
    `- ${outcome}`,
    `- EDB old: ${raw(cobol?.edbNumber)}, new: ${raw(handler?.edbNumber)}`,
    ...(isMismatch
      ? ['- Old looks products up on the first 14 characters of the article number without separators (varekey_file) and',
         '  can find another product sharing them; new finds the requested one (US 326). Confirm whether that explains it.']
      : []),
    ...(rows.length
      ? ['', '| Field | Property | Old (COBOL) | New | Kind |', '|---|---|---|---|---|', ...rows]
      : []),
    ...(differences.length ? ['', 'Findings from ProductInfoComparison for this product:', ...differences.map(d => `- ${d.text}`)] : []),
    ...(invalidText.length ? ['', ...invalidText] : []),
    '',
    'Old (COBOL) product:',
    '```json',
    JSON.stringify(cobol ?? null, null, 2),
    '```',
    'New product:',
    '```json',
    JSON.stringify(handler ?? null, null, 2),
    '```',
    '',
    '## Known, accepted differences (not bugs)',
    '- Prices within 0.02; note "0" versus empty; customs where old has extra text after the tariff; customs on products',
    '  without their own customs info (old shows the previous product\'s customs).',
    '- Stock and warehouse differences are warnings: stock can change between the calls, and new deliberately returns',
    '  only the internal (own) warehouses 10, 13, 15, 17, 18, 92 and 93 that have a name, no supplier warehouses.',
    '- A deleted product (V-SLETT \'S\') is invalid in new ("VARE ER SLETTET"), while old still returns it: a warning.',
    '- Location and text where old stops at an "&" that new has (the old connector cuts the value there).',
    '',
    '## Where to look',
    '- New: Autodata.Application/Handlers/GetProductInfos/GetProductInfosHandler.cs',
    `- Old: COBOL program ${program}, called and mapped in Shared/Services/Implementations/Cobol/ProductInfoService.cs`,
    '- Comparison (which differences are reported or tolerated): Autodata.Application/Services/ProductInfoComparison.cs',
    '- Test page endpoint: WebApi/Endpoints/ProductInfoCompareEndpoints.cs; page: Frontend/src/pages/NVCMP/components/ProductInfoCompare.tsx',
    '',
    '## Task',
    'Find the root cause of each difference (or, for a missing product or mismatch, why the two sides disagree): is the',
    'new handler wrong, does COBOL behave in a way the new handler should copy, or is the comparison itself wrong?',
    'Trace each value through the code, the COBOL source and copybooks where available, and the data for this company,',
    'customer and product (read-only - do not change the database). Report what you find with file and line references,',
    'and propose a fix with a unit test that reproduces it. Do not commit.',
  ].join('\n');
};

/** Innlogget brukers firma (Department i tokenet), brukt som forhåndsvalg. */
const loggedInCompany = (): string => {
  try {
    return CSharpUtils.T_companyID ?? '';
  } catch {
    return '';
  }
};

const ProductInfoCompare: React.FC = () => {
  const [companyId, setCompanyId] = useState(loggedInCompany);
  const [customerNumber, setCustomerNumber] = useState('');
  const [productsText, setProductsText] = useState('');
  const [adk, setAdk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ProductInfoCompareResult | null>(null);
  // Varene som ble sendt i siste sammenligning — for å finne mismatch, selv om varelisten endres etterpå.
  const [comparedProducts, setComparedProducts] = useState<Product[]>([]);
  const comparedIds = Array.from(new Set(comparedProducts.map(p => p.productId)));
  // AI-prompten som vises (for én vare), eller null.
  const [prompt, setPrompt] = useState<string | null>(null);
  const [promptCopied, setPromptCopied] = useState(false);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  // Øker for hver sammenligning, så varene lukkes/åpnes på nytt etter defaultExpanded.
  const [runId, setRunId] = useState(0);
  // Fremdrift: varene sendes i deler à BATCH_SIZE, én etter én; resultatet vises etter hver del.
  const [progress, setProgress] = useState<{ done: number; total: number; products: number } | null>(null);
  const [partErrors, setPartErrors] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const products = parseProducts(productsText);
  const companyValid = /^\d{3}$/.test(companyId);
  const canCompare = !loading && companyValid && Number(customerNumber) > 0 && products.length > 0;

  const handleCompare = async (): Promise<void> => {
    const batches: (typeof products)[] = [];
    for (let i = 0; i < products.length; i += BATCH_SIZE) batches.push(products.slice(i, i + BATCH_SIZE));

    const abort = new AbortController();
    abortRef.current = abort;
    setLoading(true);
    setError('');
    setPartErrors([]);
    setResult(null);
    setComparedProducts(products);
    setRunId(id => id + 1);
    setProgress({ done: 0, total: batches.length, products: products.length });

    const parts: ProductInfoCompareResult[] = [];
    try {
      for (let i = 0; i < batches.length && !abort.signal.aborted; i++) {
        try {
          const response = await compareProductInfo(
            { companyId, customerNumber: Number(customerNumber), products: batches[i], adk },
            abort.signal,
          );
          parts.push(response.data);
          setResult(mergeCompareResults(parts));
        } catch (err: any) {
          if (abort.signal.aborted) break;
          const status = err.response?.status;
          if (status === 403) {
            setError('Du har ikke tilgang til denne siden.');
            break;
          }
          const message = `${err.response?.data?.detail || err.response?.data || err.message || 'Feil ved sammenligning'}`;
          const from = i * BATCH_SIZE + 1;
          // Neste del kjøres likevel: én treg eller feilende del skal ikke stoppe resten.
          setPartErrors(errors => [...errors, `Del ${i + 1} (vare ${from}–${from + batches[i].length - 1}): ${message}`]);
        }
        setProgress({ done: i + 1, total: batches.length, products: products.length });
      }
    } finally {
      abortRef.current = null;
      setLoading(false);
    }
  };

  const handleCancel = (): void => abortRef.current?.abort();

  /** Fyll varelisten fra en fil. Filnavn som NVCMP_01_firma913_kunde118128_ADK.txt fyller også firma, kunde og ADK. */
  const loadFile = async (file: File): Promise<void> => {
    setProductsText(await file.text());
    const firma = /firma(\d{3})/i.exec(file.name)?.[1];
    const kunde = /kunde(\d+)/i.exec(file.name)?.[1];
    if (firma && kunde) {
      setCompanyId(firma);
      setCustomerNumber(kunde);
      setAdk(/_ADK\b/i.test(file.name.replace(/\.[^.]+$/, '')));
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // samme fil kan lastes opp igjen
    if (file) await loadFile(file);
  };

  // Slipp en fil hvor som helst i inndata-feltet (i tillegg til «Last opp fil»).
  const [dragging, setDragging] = useState(false);
  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes('Files');
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    if (!isFileDrag(e) || loading) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
  };
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
    if (!isFileDrag(e)) return;
    e.preventDefault(); // ellers åpner nettleseren filen
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && !loading) await loadFile(file);
  };

  /**
   * Lim inn en JSON-forespørsel hvor som helst i inndata-feltet: fyller firma, kunde, ADK og varene med antall.
   * Samme firma, kunde og ADK som før: varene legges til listen. Ellers erstatter de den.
   */
  const [pasteInfo, setPasteInfo] = useState('');
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (loading) return;
    const request = parsePastedRequest(e.clipboardData.getData('text'));
    if (!request) return;
    e.preventDefault();
    const same = request.companyId === companyId && request.customerNumber === customerNumber && request.adk === adk;
    setCompanyId(request.companyId);
    setCustomerNumber(request.customerNumber);
    setAdk(request.adk);
    setProductsText(formatProducts(same ? addProducts(products, request.products) : request.products));
    const varer = `${request.products.length} ${request.products.length === 1 ? 'vare' : 'varer'}`;
    setPasteInfo(same
      ? `La til ${varer} fra JSON i listen (samme firma, kunde og ADK).`
      : `Fylte inn firma ${request.companyId}, kunde ${request.customerNumber}${request.adk ? ', ADK' : ''} og ${varer} fra JSON.`);
  };

  const openPrompt = (key: string, pair: Pair): void => {
    if (!result) return;
    const edbs = new Set([pair.cobol?.edbNumber, pair.handler?.edbNumber, key].filter(Boolean));
    const idKey = (id?: string | null) => `${(id ?? '').slice(0, 3).trim().toUpperCase()}${unformat((id ?? '').slice(3))}`;
    const requested = pair.requested ? idKey(pair.requested) : null;
    const productIds = new Set([pair.cobol, pair.handler].map(p => idKey(`${(p?.alpha ?? '').padEnd(3)}${p?.productNumber ?? ''}`)));
    const isThis = (id?: string | null) => !!id && (requested ? idKey(id) === requested : productIds.has(idKey(id)));
    setPromptCopied(false);
    setPrompt(buildInvestigationPrompt({
      pair,
      key,
      companyId: result.companyId,
      customerNumber: result.customerNumber,
      adk: result.adk,
      compared: comparedProducts,
      // Mismatch: backend sammenligner ikke de to ulike varene felt for felt — bare funnene for den etterspurte varen.
      differences: (result.differences ?? []).filter(d =>
        (!pair.mismatch && !!d.edbNumber && edbs.has(d.edbNumber)) || isThis(d.productId)),
      invalid: {
        cobol: (result.cobol?.invalidProducts ?? []).filter(p => isThis(`${(p.alfa ?? '').padEnd(3)}${p.artikkelnummer ?? ''}`)),
        handler: (result.handler?.invalidProducts ?? []).filter(p => isThis(p.productId)),
      },
    }));
  };

  /** Kopier prompten; navigator.clipboard finnes bare i sikker kontekst (https/localhost), ellers markeres teksten. */
  const copyPrompt = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(prompt ?? '');
    } catch {
      promptRef.current?.select();
      document.execCommand('copy');
    }
    setPromptCopied(true);
  };

  const differencesFor = (edb: string): CompareDifference[] =>
    (result?.differences ?? []).filter(d => d.edbNumber === edb);
  /** Avvikene for varen, uten advarslene fra backend. */
  const realDifferencesFor = (edb: string): CompareDifference[] => differencesFor(edb).filter(d => !d.isWarning);
  /** Slettet vare (V-SLETT): ny gir den som ugyldig, gammel svarer med den — en advarsel, ikke et avvik. */
  const isDeleted = (edb: string): boolean => differencesFor(edb).some(d => d.isWarning && d.field === 'Deleted');

  // Varer fra begge sider, parret på EDB-nummer. Sortert: avvik, advarsler, like — deretter alfa+artikkelnummer.
  // rank: 0 = avvik (eller mangler på én side, eller mismatch), 1 = bare advarsler (lager, slettet vare), 2 = lik.
  const ranked = (() => {
    if (!result) return [];
    const map = new Map<string, Pair>();
    for (const p of result.cobol?.products ?? []) map.set(productKey(p), { ...map.get(productKey(p)), cobol: p });
    for (const p of result.handler?.products ?? []) map.set(productKey(p), { ...map.get(productKey(p)), handler: p });

    // Gammel kan svare flere etterspurte varer med samme vare (samme EDB), og gjentar da hvert etterspurte nummer.
    // Raden for EDB-nummeret skal vise gammels svar for samme artikkelnummer som ny — ikke det siste med samme EDB.
    for (const [k, pair] of map) {
      const handler = pair.handler;
      if (!pair.cobol || !handler || unformat(pair.cobol.productNumber) === unformat(handler.productNumber)) continue;
      const sameNumber = (result.cobol?.products ?? []).find(
        p => productKey(p) === k && unformat(p.productNumber) === unformat(handler.productNumber));
      if (sameNumber) map.set(k, { ...pair, cobol: sameNumber });
    }

    // Mismatch: varekey_file har bare de første 14 tegnene, så gammel kan svare med en annen vare som deler dem,
    // mens ny finner den etterspurte. Det blir én rad for den etterspurte varen, ikke to som mangler på hver sin side.
    for (const id of comparedIds) {
      const alpha = id.slice(0, 3).trim().toUpperCase();
      const number = unformat(id.slice(3));
      if (number.length <= LOOKUP_LENGTH) continue;
      const sameAlpha = (p: CompareProduct) => (p.alpha ?? '').trim().toUpperCase() === alpha;
      const handler = (result.handler?.products ?? []).find(p => sameAlpha(p) && unformat(p.productNumber) === number);
      const cobolProducts = (result.cobol?.products ?? []).filter(sameAlpha);
      if (!handler || cobolProducts.some(p => p.edbNumber === handler.edbNumber)) continue;
      // Gammel svarer med etterspurt artikkelnummer (WWWV956 o.fl. gjentar det, og har varens eget i alt.-feltene)
      // eller med varens eget, som deler de 14 første tegnene — men med en annen vare (EDB).
      const cobol = cobolProducts.find(p => unformat(p.productNumber) === number)
        ?? cobolProducts.find(p => unformat(p.productNumber).slice(0, LOOKUP_LENGTH) === number.slice(0, LOOKUP_LENGTH));
      if (!cobol) continue;
      // Radene som bare fantes på én side, forklares av mismatchen. Gammels vare beholder sin egen rad
      // når ny også fant den (den ble etterspurt selv).
      if (!map.get(productKey(handler))?.cobol) map.delete(productKey(handler));
      if (!map.get(productKey(cobol))?.handler) map.delete(productKey(cobol));
      map.set(`mismatch:${id}`, { cobol, handler, requested: id, mismatch: true });
    }

    const rank = ([key, pair]: [string, Pair]) => {
      if (pair.mismatch) return 0;
      const edb = pair.cobol?.edbNumber ?? pair.handler?.edbNumber ?? key;
      const deleted = isDeleted(edb);
      if (!pair.cobol || (!pair.handler && !deleted) || realDifferencesFor(edb).some(d => d.field)) return 0;
      return deleted || warningCountFor(pair.cobol, pair.handler) > 0 ? 1 : 2;
    };
    const alphaArt = ({ cobol, handler, requested }: Pair) =>
      requested ?? `${(cobol ?? handler)?.alpha ?? ''}${(cobol ?? handler)?.productNumber ?? ''}`.trim();
    const collator = new Intl.Collator('nb', { numeric: true, sensitivity: 'base' });
    return Array.from(map.entries())
      .map(entry => ({ entry, rank: rank(entry), alphaArt: alphaArt(entry[1]) }))
      .sort((a, b) => a.rank - b.rank || collator.compare(a.alphaArt, b.alphaArt));
  })();
  const pairs = ranked.map(r => r.entry);
  // Oppsummeringen teller varer, ikke enkeltfelt: hver vare havner i nøyaktig én av gruppene.
  const productsWithDiffs = ranked.filter(r => r.rank === 0).length;
  const productsWithWarnings = ranked.filter(r => r.rank === 1).length;
  const productsEqual = ranked.filter(r => r.rank === 2).length;
  const productsMismatched = pairs.filter(([, pair]) => pair.mismatch).length;
  // Generelle: uten felt, eller knyttet til en vare som ikke vises (f.eks. Status for en vare ingen av sidene fant).
  const shownEdbs = new Set(pairs.flatMap(([key, pair]) =>
    pair.mismatch ? [pair.cobol?.edbNumber, pair.handler?.edbNumber] : [pair.cobol?.edbNumber ?? pair.handler?.edbNumber ?? key]));
  // «Cobol/Handler is missing edb» for varene i en mismatch er forklart av den — ikke generelle avvik.
  const pairedEdbs = new Set(pairs.filter(([, pair]) => !pair.mismatch).map(([key, pair]) => pair.cobol?.edbNumber ?? pair.handler?.edbNumber ?? key));
  const mismatchOnlyEdbs = new Set(pairs.filter(([, pair]) => pair.mismatch)
    .flatMap(([, pair]) => [pair.cobol?.edbNumber, pair.handler?.edbNumber])
    .filter((edb): edb is string => !!edb && !pairedEdbs.has(edb)));
  // Generelle avvik for en vare i en mismatch skjules: backend merker dem med varens alfa+artikkelnummer, som for gammel er
  // det etterspurte (gjentatt) og for ny den etterspurte varen. Sammenlignes uten skilletegn.
  const idKey = (id?: string | null) => `${(id ?? '').slice(0, 3).trim().toUpperCase()}${unformat((id ?? '').slice(3))}`;
  const mismatchIds = new Set(pairs.filter(([, pair]) => pair.mismatch).map(([, pair]) => idKey(pair.requested)));
  const generalDifferences = (result?.differences ?? [])
    .filter(d => !d.isWarning)
    .filter(d => !d.field || !d.edbNumber || !shownEdbs.has(d.edbNumber))
    .filter(d => d.field || !d.edbNumber || !mismatchOnlyEdbs.has(d.edbNumber))
    .filter(d => !d.productId || !mismatchIds.has(idKey(d.productId)));
  const soFar = loading ? ' så langt' : '';

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" gutterBottom>Sammenlign vareinfo – gammel (COBOL) og ny</Typography>

      <Paper
        sx={{
          p: 2,
          mb: 2,
          outline: dragging ? '2px dashed' : 'none',
          outlineColor: 'primary.main',
          bgcolor: dragging ? 'action.hover' : undefined,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onPaste={handlePaste}
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Firma"
              value={companyId}
              onChange={e => setCompanyId(e.target.value.replace(/\D/g, '').slice(0, 3))}
              error={companyId !== '' && !companyValid}
              helperText={companyId !== '' && !companyValid ? 'Tre siffer' : undefined}
              size="small"
              sx={{ width: 120 }}
            />
            <TextField
              label="Kundenummer"
              value={customerNumber}
              onChange={e => setCustomerNumber(e.target.value.replace(/\D/g, ''))}
              size="small"
              sx={{ width: 200 }}
            />
            <FormControlLabel control={<Checkbox checked={adk} onChange={e => setAdk(e.target.checked)} />} label="AD-kasse (ADK)" />
          </Stack>
          <TextField
            label="Varer – én per linje: alfa+artikkelnummer, eventuelt antall (f.eks. FOM123100 2) — eller lim inn en JSON-forespørsel"
            value={productsText}
            onChange={e => {
              setProductsText(e.target.value);
              setPasteInfo('');
            }}
            multiline
            minRows={4}
            maxRows={14}
            fullWidth
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="contained" startIcon={<CompareArrowsIcon />} disabled={!canCompare} onClick={handleCompare}>
              Sammenlign
            </Button>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={loading}>
              Last opp fil
              <input hidden type="file" accept=".txt,.csv,text/plain" onChange={handleFile} />
            </Button>
            {loading && <CircularProgress size={24} />}
            <Typography variant="body2" color="text.secondary">
              {dragging ? 'Slipp filen for å fylle varelisten' : `${products.length} varer — eller slipp en fil her`}
            </Typography>
          </Stack>
          {pasteInfo && <Alert severity="success" onClose={() => setPasteInfo('')}>{pasteInfo}</Alert>}
          <Alert severity="info">
            Røde felt er avvik mellom gammel og ny. Gule linjer (lager, totalt lager og hvert lager) er advarsler, ikke avvik: beholdningen kan endre
            seg mellom kallene, og gammel og ny tar med ulike lager (et lager som bare finnes på den ene siden gir
            advarsel, også med beholdning 0). Disse forskjellene er kjente og
            regnes ikke som avvik: priser innenfor 0,02; merknad «0» mot tom; toll der gammel har ekstra tekst etter
            tolltariffen; toll på varer uten egne tollopplysninger (gammel viser forrige vares toll); lagerplass og tekst der
            gammel stopper ved en «&» som ny har (gammel kobling kutter verdien der). En slettet vare er ugyldig i ny, mens
            gammel fortsatt svarer med den: advarsel, ikke avvik.
            «Mismatch»: gammel og ny svarte med hver sin vare for samme etterspurte vare. Gammel slår opp på de 14 første
            tegnene av artikkelnummeret og kan finne en annen vare som deler dem; ny finner den etterspurte (US 326).
          </Alert>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {progress && (loading || progress.total > 1) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} />
            </Box>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {loading
                ? `Del ${Math.min(progress.done + 1, progress.total)} av ${progress.total} — ${Math.min(progress.done * BATCH_SIZE, progress.products)} av ${progress.products} varer ferdig`
                : `${progress.done} av ${progress.total} deler kjørt (${BATCH_SIZE} varer per del)`}
            </Typography>
            {loading && <Button size="small" onClick={handleCancel}>Avbryt</Button>}
          </Stack>
          {partErrors.map((e, i) => (
            <Alert key={i} severity="error" sx={{ mt: 1 }}>{e}</Alert>
          ))}
        </Paper>
      )}

      {result && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
              {result.areEqual === true && productsWithWarnings === 0 && (
                <Chip color="success" label={loading ? 'Ingen avvik så langt' : 'Ingen avvik'} />
              )}
              {result.areEqual == null && <Chip color="warning" label="Kunne ikke sammenligne alt" />}
              {productsWithDiffs > 0 && (
                <Chip color="error" label={`${productsWithDiffs} ${productsWithDiffs === 1 ? 'vare' : 'varer'} med avvik${soFar}`} />
              )}
              {productsMismatched > 0 && (
                <Chip color="error" variant="outlined" label={`${productsMismatched} mismatch (av dem)`} />
              )}
              {productsWithWarnings > 0 && (
                <Chip
                  sx={{ bgcolor: WARN_BACKGROUND }}
                  label={`${productsWithWarnings} ${productsWithWarnings === 1 ? 'vare' : 'varer'} med bare advarsler${soFar}`}
                />
              )}
              {productsEqual > 0 && (productsWithDiffs > 0 || productsWithWarnings > 0) && (
                <Chip color="success" variant="outlined" label={`${productsEqual} like`} />
              )}
              {generalDifferences.length > 0 && (
                <Chip color="error" variant="outlined" label={`${generalDifferences.length} generelle avvik`} />
              )}
              <Typography variant="body2">Firma {result.companyId}, kunde {result.customerNumber}{result.adk ? ', ADK' : ''}</Typography>
              <Typography variant="body2">
                Gammel: {formatDuration(result.cobolElapsedMs)}
                {(result.cobolBatchElapsedMs?.length ?? 0) > 1 && ` (${callSummary(result.cobolBatchElapsedMs!)})`}
              </Typography>
              <Typography variant="body2">Ny: {formatDuration(result.handlerElapsedMs)}</Typography>
            </Stack>
            {result.cobolError && <Alert severity="error" sx={{ mt: 2 }}>Gammel feilet: {result.cobolError}</Alert>}
            {result.handlerError && <Alert severity="error" sx={{ mt: 2 }}>Ny feilet: {result.handlerError}</Alert>}
            {(result.cobol?.status?.status || result.handler?.status?.status) && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Status – gammel: «{result.cobol?.status?.status ?? ''}», ny: «{result.handler?.status?.status ?? ''}»
              </Typography>
            )}
            {((result.cobol?.invalidProducts?.length ?? 0) > 0 || (result.handler?.invalidProducts?.length ?? 0) > 0) && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Ugyldige varer – gammel: {(result.cobol?.invalidProducts ?? []).map(p => `${p.alfa ?? ''}${p.artikkelnummer ?? ''}`).join(', ') || 'ingen'};
                ny: {(result.handler?.invalidProducts ?? []).map(p => `${p.productId ?? ''} (${p.feilmelding ?? ''})`).join(', ') || 'ingen'}
              </Typography>
            )}
            {generalDifferences.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Generelle avvik</Typography>
                {generalDifferences.map((d, i) => <Typography key={i} variant="body2" color="error">{d.text}</Typography>)}
              </Box>
            )}
          </Paper>

          {pairs.map(([key, pair]) => {
            const isMismatch = !!pair.mismatch;
            // Mismatch: to ulike varer, så backend har ingen feltavvik for paret. Bare feltene i MISMATCH_FIELDS som er
            // ulike markeres; resten vises uten sammenligning.
            const edb = pair.cobol?.edbNumber ?? pair.handler?.edbNumber ?? key;
            const diffs = isMismatch ? [] : realDifferencesFor(edb);
            const deleted = !isMismatch && isDeleted(edb);
            const diffFields = isMismatch
              ? new Set(FIELDS.filter(f => MISMATCH_FIELDS.has(f.key) && show(f.value(pair.cobol!)) !== show(f.value(pair.handler!))).map(f => f.key))
              : new Set(diffs.map(d => d.field).filter(Boolean));
            const p = pair.cobol ?? pair.handler;
            const unknownDiffs = diffs.filter(d => d.field && !FIELDS.some(f => f.key === d.field));
            const hasDiffs = isMismatch || diffFields.size > 0 || !pair.cobol || (!pair.handler && !deleted);
            const diffLabels = isMismatch ? [] : Array.from(diffFields).map(field => FIELDS.find(f => f.key === field)?.label ?? field);
            // Lageradvarsler gir ikke mening mellom to ulike varer: vist, men ikke markert.
            const warehouses = warehouseRows(pair.cobol, pair.handler).map(w => (isMismatch ? { ...w, isWarning: false } : w));
            const stockWarnings = isMismatch ? new Set<string>() : stockFieldWarnings(pair.cobol, pair.handler);
            const warnings = isMismatch ? 0 : warningCountFor(pair.cobol, pair.handler);
            const headerBackground = hasDiffs ? DIFF_BACKGROUND : warnings > 0 || deleted ? WARN_BACKGROUND : undefined;
            const title = `${`${p?.alpha ?? ''} ${p?.productNumber ?? ''}`.trim()} – ${p?.productDescription ?? ''} (EDB ${key})`;
            const nameAndEdb = (side?: CompareProduct) => `${side?.productDescription?.trim() ?? ''} (${side?.edbNumber ?? ''})`;
            return (
              <Accordion
                key={`${runId}-${key}`}
                defaultExpanded={pairs.length === 1}
                disableGutters
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={headerBackground ? { bgcolor: headerBackground } : undefined}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ width: '100%' }}>
                    {isMismatch ? (
                      // <etterspurt alfa> <artikkelnummer> – [Gammel] <navn> (<EDB>), [Ny] <navn> (<EDB>) [Mismatch]
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle1">
                          {`${pair.requested!.slice(0, 3).trim()} ${pair.requested!.slice(3).trim()} –`}
                        </Typography>
                        <Chip size="small" variant="outlined" label="Gammel" />
                        <Typography variant="subtitle1">{`${nameAndEdb(pair.cobol)},`}</Typography>
                        <Chip size="small" variant="outlined" label="Ny" />
                        <Typography variant="subtitle1">{nameAndEdb(pair.handler)}</Typography>
                        <Chip size="small" color="error" label="Mismatch" />
                      </Stack>
                    ) : (
                      <Typography variant="subtitle1">{title}</Typography>
                    )}
                    {!isMismatch && diffFields.size > 0 && <Chip size="small" color="error" label={`Har avvik (${diffFields.size})`} />}
                    {!hasDiffs && !deleted && <Chip size="small" color="success" label="Lik" />}
                    {warnings > 0 && (
                      <Chip size="small" variant="outlined" color="warning" label={`Lageradvarsel (${warnings})`} />
                    )}
                    {!pair.cobol && <Chip size="small" color="warning" label="Mangler i gammel" />}
                    {!pair.handler && (deleted
                      ? <Chip size="small" variant="outlined" color="warning" label="Slettet – ugyldig i ny" />
                      : <Chip size="small" color="warning" label="Mangler i ny" />)}
                    {diffLabels.length > 0 && (
                      <Typography variant="body2" color="error">{diffLabels.join(', ')}</Typography>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Lag en AI-prompt for å undersøke denne varen">
                      <IconButton
                        size="small"
                        aria-label="Lag AI-prompt"
                        // Ikke åpne/lukk varen når knappen trykkes.
                        onClick={e => {
                          e.stopPropagation();
                          openPrompt(key, pair);
                        }}
                        onFocus={e => e.stopPropagation()}
                      >
                        <SmartToyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '28%' }}>Felt</TableCell>
                      <TableCell>Gammel (COBOL)</TableCell>
                      <TableCell>Ny</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{NAMES_DIFFER ? 'Egenskap (gammel / ny)' : 'Egenskap'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {FIELDS.map(f => {
                      const isDiff = diffFields.has(f.key);
                      const isWarning = !isDiff && stockWarnings.has(f.key);
                      const cobolValue = pair.cobol ? show(f.value(pair.cobol)) : ''; // varen mangler på denne siden: ingen verdier
                      const handlerValue = pair.handler ? show(f.value(pair.handler)) : '';
                      if (!isDiff && cobolValue === '' && handlerValue === '') return null; // tomme felt skjules
                      const background = isDiff ? DIFF_BACKGROUND
                        : isWarning ? WARN_BACKGROUND
                        : isMismatch && !MISMATCH_FIELDS.has(f.key) ? UNRELATED_BACKGROUND
                        : undefined;
                      return (
                        <TableRow key={f.key} sx={background ? { bgcolor: background } : undefined}>
                          <TableCell sx={{ fontWeight: isDiff || isWarning ? 600 : undefined }}>{f.label}</TableCell>
                          <TableCell>{cobolValue}</TableCell>
                          <TableCell>{handlerValue}</TableCell>
                          <TableCell sx={PROPERTY_CELL}>{propertyNames(f.oldName ?? f.key, f.newName ?? f.key)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {warehouses.map((w, i) => (
                      <TableRow
                        key={`w-${i}`}
                        sx={w.isWarning ? { bgcolor: WARN_BACKGROUND } : isMismatch ? { bgcolor: UNRELATED_BACKGROUND } : undefined}
                      >
                        <TableCell sx={{ fontWeight: w.isWarning ? 600 : undefined }}>Lager: {w.label}</TableCell>
                        <TableCell>{w.cobol}</TableCell>
                        <TableCell>{w.handler}</TableCell>
                        <TableCell sx={PROPERTY_CELL}>{propertyNames('Warehouses[].Stock', 'Warehouses[].Stock')}</TableCell>
                      </TableRow>
                    ))}
                    {unknownDiffs.map((d, i) => (
                      <TableRow key={`u${i}`} sx={{ bgcolor: DIFF_BACKGROUND }}>
                        <TableCell sx={{ fontWeight: 600 }}>{d.field}</TableCell>
                        <TableCell>{d.cobol}</TableCell>
                        <TableCell>{d.handler}</TableCell>
                        <TableCell sx={PROPERTY_CELL}>{propertyNames(d.field ?? '', d.field ?? '')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )}

      <Dialog open={prompt !== null} onClose={() => setPrompt(null)} maxWidth="md" fullWidth>
        <DialogTitle>AI-prompt for å undersøke varen</DialogTitle>
        <DialogContent>
          <TextField
            value={prompt ?? ''}
            inputRef={promptRef}
            multiline
            fullWidth
            minRows={12}
            maxRows={24}
            slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          {promptCopied && <Typography variant="body2" color="success.main" sx={{ mr: 'auto', ml: 2 }}>Kopiert</Typography>}
          <Button onClick={() => setPrompt(null)}>Lukk</Button>
          <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={copyPrompt}>Kopier</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductInfoCompare;
